import { useEffect, useMemo, useState } from 'react'
import { Leaf, Loader2 } from 'lucide-react'
import { Button } from '../ui/Button'
import { HabitAdjustmentDialog } from './HabitAdjustmentDialog'
import type {
  HabitAdjustmentCheckin,
  HabitAdjustmentChooseActionResponse,
  HabitAdjustmentConfirmResponse,
  HabitAdjustmentDecisionResponse,
  HabitAdjustmentRespondResponse,
  HabitAdjustmentSimpleResponse,
} from '../../services/habitAdjustmentService'

type DialogStep = 'diagnose' | 'pause'

// Two static templates so Tailwind can see both class strings. The "why"
// column disappears entirely when no habit on screen has a captured reason.
const GRID_WITH_WHY =
  'grid grid-cols-[minmax(0,1.1fr)_minmax(0,1.5fr)_minmax(9rem,0.7fr)_auto] gap-3'
const GRID_NO_WHY = 'grid grid-cols-[minmax(0,1.6fr)_minmax(9rem,0.7fr)_auto] gap-3'

function getWhyText(checkin: HabitAdjustmentCheckin): string | null {
  // Defect 2026-08-29: this used to return 'No original reason captured yet.'
  // — an internal note about OUR missing data, printed to the user under a
  // heading. When there is nothing captured, the field is omitted instead.
  const description = checkin.habit_description?.trim()
  return description ? description : null
}

function getRecentPattern(checkin: HabitAdjustmentCheckin) {
  // Framed as recent rhythm, not a scorecard (matches the wording in
  // HabitAdjustmentCheckinCard). Defect 2026-08-29: this read "Missed 10 of
  // 14", a deficit verdict on the user, under a column headed "Missed".
  const missed = typeof checkin.miss_count === 'number' ? checkin.miss_count : null
  const expected = typeof checkin.expected_count === 'number' ? checkin.expected_count : null

  if (missed !== null && expected !== null && expected > 0) {
    const landed = Math.max(expected - missed, 0)
    return `${landed} of ${expected} lately`
  }
  if (missed !== null && expected !== null) return `${Math.max(expected - missed, 0)} lately`
  return 'A rough stretch'
}

function getDetectedWindow(checkin: HabitAdjustmentCheckin) {
  const start = checkin.detected_window_start?.slice(0, 10)
  const end = checkin.detected_window_end?.slice(0, 10)
  if (start && end) return `${start} to ${end}`
  if (end) return `Through ${end}`
  return null
}

function getBody(checkin: HabitAdjustmentCheckin) {
  // The server composes a per-habit body (its own pattern + its own name);
  // this fallback only fires if that copy is missing entirely.
  return checkin.body || `We can make ${checkin.habit_title} easier without throwing it away.`
}

export function HabitAdjustmentCheckinList({
  checkins,
  markShown,
  respond,
  diagnose,
  chooseAction,
  confirm,
  pause,
  onDone,
}: {
  checkins: HabitAdjustmentCheckin[]
  markShown: (checkinId: string) => void
  respond: (
    checkinId: string,
    response: 'adjust' | 'pause' | 'keep_as_is' | 'not_now',
  ) => Promise<HabitAdjustmentRespondResponse>
  diagnose: (checkinId: string, reasons: string[]) => Promise<HabitAdjustmentDecisionResponse>
  chooseAction: (
    decisionId: string,
    actionId: string,
    details?: Record<string, unknown>,
  ) => Promise<HabitAdjustmentChooseActionResponse>
  confirm: (decisionId: string) => Promise<HabitAdjustmentConfirmResponse>
  pause: (
    habitDefId: number,
    payload: { paused_until?: string; reason_codes?: string[]; source?: string },
  ) => Promise<HabitAdjustmentSimpleResponse>
  onDone: () => void
}) {
  const [openCheckin, setOpenCheckin] = useState<HabitAdjustmentCheckin | null>(null)
  const [initialStep, setInitialStep] = useState<DialogStep>('diagnose')
  const [workingId, setWorkingId] = useState<string | null>(null)
  // RESTRAINT (defect 2026-08-29): the panel used to stack every open
  // check-in, handing a struggling user five habits x four actions in one
  // view. We ask about ONE; the rest stay in the payload and are one pull
  // away — nothing is hidden, only un-asked.
  const [expanded, setExpanded] = useState(false)

  const visible = useMemo(
    () => (expanded ? checkins : checkins.slice(0, 1)),
    [checkins, expanded],
  )
  const remaining = checkins.length - visible.length
  const visibleKey = visible.map(checkin => checkin.id).join(',')

  useEffect(() => {
    // Only the check-ins actually on screen are marked shown — claiming a
    // view that never happened would poison the cooldown and telemetry.
    visible.forEach(checkin => markShown(checkin.id))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleKey, markShown])

  const openFlow = async (checkin: HabitAdjustmentCheckin, step: DialogStep) => {
    setInitialStep(step)
    setWorkingId(`${checkin.id}:${step}`)
    try {
      await respond(checkin.id, step === 'pause' ? 'pause' : 'adjust').catch(() => undefined)
    } finally {
      setWorkingId(null)
      setOpenCheckin(checkin)
    }
  }

  const completeQuickAction = async (
    checkin: HabitAdjustmentCheckin,
    response: 'keep_as_is' | 'not_now',
  ) => {
    setWorkingId(`${checkin.id}:${response}`)
    try {
      await respond(checkin.id, response).catch(() => undefined)
      onDone()
    } finally {
      setWorkingId(null)
    }
  }

  const renderActions = (checkin: HabitAdjustmentCheckin) => {
    const isWorking = workingId?.startsWith(`${checkin.id}:`)
    // No height override here: size="sm" carries min-h-touch (44px), and an
    // h-8 override used to fight it (defect 2026-08-29).
    return (
      <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:justify-end">
        <Button
          size="sm"
          disabled={isWorking}
          onClick={() => void openFlow(checkin, 'diagnose')}
          className="bg-brand hover:bg-brand-hover text-on-brand text-xs"
        >
          Adjust
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isWorking}
          onClick={() => void openFlow(checkin, 'pause')}
          className="text-xs"
        >
          Pause
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={isWorking}
          onClick={() => void completeQuickAction(checkin, 'keep_as_is')}
          className="text-xs"
        >
          Keep
        </Button>
        <Button
          size="sm"
          variant="ghost"
          disabled={isWorking}
          onClick={() => void completeQuickAction(checkin, 'not_now')}
          className="text-xs"
        >
          Later
        </Button>
      </div>
    )
  }

  if (checkins.length === 0) return null

  const showWhyColumn = visible.some(checkin => getWhyText(checkin) !== null)
  const gridClass = showWhyColumn ? GRID_WITH_WHY : GRID_NO_WHY

  return (
    <>
      {/* @container, not a viewport breakpoint. Defect found 2026-08-29 by
          tests/ui-behavior/02-layout-integrity: this panel lives in Today's
          right rail, which is 288px wide on a 1440px screen. The table below
          was gated on `md:` — a VIEWPORT width — so a wide screen switched on
          a three-track grid inside a narrow rail, the 9rem floor on the
          pattern column plus the decision buttons ate the whole width, and
          the habit's sentence collapsed into a one-word-per-line column:
          "7 / of / 14 / landed / lately." The table must depend on the space
          it actually has. */}
      <div className="@container rounded-xl border border-soft bg-surface overflow-hidden">
        <div className="flex items-start justify-between gap-3 border-b border-soft px-3 py-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <Leaf className="w-4 h-4 text-brand flex-shrink-0" />
              <p className="text-sm font-semibold text-primary">Habits that need a gentler plan</p>
            </div>
            <p className="mt-1 text-xs text-secondary">
              {checkins.length === 1
                ? 'One habit had a rough stretch. Adjust it now or keep it for later.'
                : `${checkins.length} habits had a rough stretch. Here is one — the others stay put until you want them.`}
            </p>
          </div>
          {workingId && <Loader2 className="w-4 h-4 text-brand animate-spin flex-shrink-0 mt-0.5" />}
        </div>

        <div className="hidden @xl:block">
          <div
            className={`${gridClass} px-3 py-2 bg-surface-alt text-app-micro font-medium uppercase tracking-wide text-muted`}
          >
            <span>Habit</span>
            {showWhyColumn && <span>Why it matters</span>}
            <span>Recent pattern</span>
            <span className="text-right">Decision</span>
          </div>
          {visible.map(checkin => (
            <div
              key={checkin.id}
              className={`${gridClass} px-3 py-3 border-t border-soft items-center`}
            >
              <div className="min-w-0">
                <p className="text-sm font-semibold text-primary truncate">{checkin.habit_title}</p>
                <p className="mt-0.5 text-xs text-secondary">{getBody(checkin)}</p>
              </div>
              {showWhyColumn &&
                (getWhyText(checkin) ? (
                  <p className="text-xs text-secondary leading-relaxed">{getWhyText(checkin)}</p>
                ) : (
                  <span aria-hidden="true" />
                ))}
              <div>
                <p className="text-xs font-medium text-primary">{getRecentPattern(checkin)}</p>
                {getDetectedWindow(checkin) && (
                  <p className="mt-0.5 text-app-micro text-muted">{getDetectedWindow(checkin)}</p>
                )}
              </div>
              {renderActions(checkin)}
            </div>
          ))}
        </div>

        <div className="@xl:hidden divide-y divide-soft">
          {visible.map(checkin => (
            <div key={checkin.id} className="p-3">
              <div className="flex items-start gap-2">
                <Leaf className="w-4 h-4 text-brand flex-shrink-0 mt-0.5" />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-primary">{checkin.habit_title}</p>
                  <p className="mt-1 text-xs text-secondary leading-relaxed">{getBody(checkin)}</p>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="rounded-lg border border-soft bg-surface-alt px-2 py-1.5">
                  <p className="text-app-micro font-medium uppercase tracking-wide text-muted">Recent pattern</p>
                  <p className="mt-0.5 text-xs font-semibold text-primary">{getRecentPattern(checkin)}</p>
                </div>
                <div className="rounded-lg border border-soft bg-surface-alt px-2 py-1.5">
                  <p className="text-app-micro font-medium uppercase tracking-wide text-muted">Window</p>
                  <p className="mt-0.5 text-xs font-semibold text-primary">
                    {getDetectedWindow(checkin) || 'Recent stretch'}
                  </p>
                </div>
              </div>
              {getWhyText(checkin) && (
                <div className="mt-3 rounded-lg border border-soft bg-surface-alt px-2 py-2">
                  <p className="text-app-micro font-medium uppercase tracking-wide text-muted">Why it matters</p>
                  <p className="mt-1 text-xs text-secondary leading-relaxed">{getWhyText(checkin)}</p>
                </div>
              )}
              <div className="mt-3">{renderActions(checkin)}</div>
            </div>
          ))}
        </div>

        {remaining > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="w-full min-h-touch border-t border-soft px-3 py-3 text-left text-xs font-medium text-brand hover:bg-surface-alt"
          >
            {remaining} more when you are ready
          </button>
        )}
        {expanded && checkins.length > 1 && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="w-full min-h-touch border-t border-soft px-3 py-3 text-left text-xs font-medium text-secondary hover:bg-surface-alt"
          >
            Show one at a time
          </button>
        )}
      </div>

      {openCheckin && (
        <HabitAdjustmentDialog
          open={!!openCheckin}
          onOpenChange={(open) => {
            if (!open) setOpenCheckin(null)
          }}
          checkin={openCheckin}
          initialStep={initialStep}
          respond={respond}
          diagnose={diagnose}
          chooseAction={chooseAction}
          confirm={confirm}
          onPause={pause}
          onDone={() => {
            setOpenCheckin(null)
            onDone()
          }}
        />
      )}
    </>
  )
}
